import React from 'react';
import { IBlock, IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { mergeBlock } from '@core/utils/mergeBlock';
import { t, resolvePreviewColor } from '@core/utils';
import { normalizeLinkHref } from '@core/utils/normalizeLinkHref';
import { BasicBlock } from '@core/components/BasicBlock';

export type INavbar = IBlockData<
	{
		align?: string;
		hamburger?: string;
		'ico-align'?: string;
		'ico-color'?: string;
		'ico-font-size'?: string;
		'ico-line-height'?: string;
		'ico-padding'?: string;
		'ico-text-decoration'?: string;
		'ico-text-transform'?: string;
	},
	{
		links: Array<{
			content: string;
			color?: string;
			href?: string;
			'font-family'?: string;
			'font-size'?: string;
			'font-style'?: string;
			'font-weight'?: string;
			'letter-spacing'?: string;
			'line-height'?: string;
			'text-decoration'?: string;
			'text-transform'?: string;
			'background-color'?: string;
			target?: string;
			padding?: string;
		}>;
	}
>;

export const Navbar: IBlock<INavbar> = createBlock({
	get name() {
		return t('Navbar');
	},
	type: BasicType.NAVBAR,
	create: payload => {
		const defaultData: INavbar = {
			type: BasicType.NAVBAR,
			data: {
				value: {
					links: [
						{
							href: '/view-listings',
							content: 'View listings',
							target: '_blank',
							padding: '15px 10px 15px 10px',
							'background-color': '#FFFFFF',
							'text-transform': 'uppercase',
							color: '#000000',
						},
						{
							href: '/home-valuation',
							content: 'Home Valuation',
							target: '_blank',
							padding: '15px 10px 15px 10px',
							'background-color': '#FFFFFF',
							'text-transform': 'uppercase',
							color: '#000000',
						},
						{
							href: '/market-updates',
							content: 'Market Updates',
							target: '_blank',
							padding: '15px 10px 15px 10px',
							'background-color': '#FFFFFF',
							'text-transform': 'uppercase',
							color: '#000000',
						},
						{
							href: '/lets-talk',
							content: "Let's Talk",
							target: '_blank',
							padding: '15px 10px 15px 10px',
							'background-color': '#FFFFFF',
							'text-transform': 'uppercase',
							color: '#000000',
						},
					],
				},
			},
			attributes: {
				align: 'center',
			},
			children: [],
		};
		return mergeBlock(defaultData, payload);
	},
	validParentType: [BasicType.COLUMN, BasicType.HERO],

	render(params) {
		const { data, context, idx } = params;
		const linkItems = (data as INavbar).data.value.links;
		const idxPrefix = idx
			? `bay-nav-${idx
					.replace(/[.[\]]/g, '-')
					.replace(/-+/g, '-')
					.replace(/(^-|-$)/g, '')}`
			: 'bay-nav';
		const pageDefaults = context?.data?.value || {};
		// With a base-url, MJML prefixes it onto every link href, so the hrefs are
		// paths by design and must not be made absolute.
		const hasBaseUrl = Boolean(String((data.attributes as any)?.['base-url'] || '').trim());
		const links = linkItems
			.map((link, linkIndex) => {
				const resolvedColor = resolvePreviewColor(
					link.color || pageDefaults['text-color'],
					params.mode,
					params.dataSource,
				);
				const normalizedLink = {
					...link,
					href: (hasBaseUrl ? String(link.href ?? '') : normalizeLinkHref(link.href)).replace(/"/g, ''),
					'font-family': link['font-family'] || pageDefaults['font-family'],
					'font-size': link['font-size'] || pageDefaults['font-size'],
					'font-weight': link['font-weight'] || pageDefaults['font-weight'],
					'line-height': link['line-height'] || pageDefaults['line-height'],
					color: resolvedColor,
				};

				const linkAttributeStr = Object.keys(normalizedLink)
					.filter(
						key =>
							key !== 'content' &&
							key !== 'background-color' &&
							normalizedLink[key as keyof typeof normalizedLink] !== '',
					) // filter att=""  and background-color (handled via css-class in mj-head)
					.map(key => `${key}="${normalizedLink[key as keyof typeof normalizedLink]}"`)
					.join(' ');

				const bg = resolvePreviewColor(
					link['background-color'],
					params.mode,
					params.dataSource,
				);
				const cssClass =
					bg && bg !== '' && bg !== 'transparent'
						? ` css-class="${idxPrefix}-${linkIndex}"`
						: '';

				return `
          <mj-navbar-link ${linkAttributeStr}${cssClass}>${normalizedLink.content || 'NEW LINK'}</mj-navbar-link>
          `;
			})
			.join('\n');
		return (
			<BasicBlock
				params={params}
				tag='mj-navbar'
			>
				{links}
			</BasicBlock>
		);
	},
});
